import { useState } from "react"

import { Box, Stack, Typography } from "@mui/material"
import { styled } from "@mui/material/styles"
import {
  Button,
  Container,
  FC,
  Link,
  ResponsiveImage,
  SvgIcon,
  type ResponsiveImageType,
  InquiryModal,
} from ".."
import { LinkType } from "../../graphql/types"

export type DestinationsGridItemProps = {
  link: LinkType
  image: ResponsiveImageType
}

export type DestinationsGridProps = {
  items: DestinationsGridItemProps[]
}

import contactImage from "../../../static/images/staff-type/goway-travel-cappadocia-turkey.jpg"

const DestinationsGrid: FC<DestinationsGridProps> = ({ items }) => {
  return (
    <DestinationsGridSection>
      <Container maxWidth={1300} overflow="hidden">
        <Box
          display="grid"
          gridTemplateColumns={{
            xs: "repeat(2, minmax(0, 1fr))",
            md: "repeat(3, minmax(0, 1fr))",
          }}
          gap={{ xs: 3, md: 7.5 }}
        >
          {items?.map(({ link, image }, i) => (
            <DestinationsGridItem key={i} link={link} image={image} />
          ))}
          {/* <DestinationsGridContactItem /> */}
        </Box>
      </Container>
    </DestinationsGridSection>
  )
}

export default DestinationsGrid

/** A sub-component for the Destinations Grid Container */
const DestinationsGridSection = styled("section")(
  ({ theme }) => `
    padding-bottom: ${theme.spacing(25)};
    position: relative;
    overflow: hidden;

    ${theme.breakpoints.up("md")} {
      padding-bottom: ${theme.spacing(36)};
    }
  `
)

/** A sub-component for the Destinations Grid Item */
const DestinationsGridItem: FC<DestinationsGridItemProps> = ({ image, link }) => {
  return (
    <Box
      position="relative"
      overflow="hidden"
      borderRadius={1.5}
      sx={{ "&:hover": { img: { transform: "scale(1.05)" } } }}
    >
      <Link type="overlay" href={link?.uri} aria-label={link?.title} />
      <Box
        sx={{
          aspectRatio: { xs: "1", md: "20 / 23" },
        }}
      >
        <ResponsiveImage
          image={image}
          display="block"
          sx={(theme) => ({
            width: "100%",
            transition: (theme) =>
              theme.transitions.create("transform", {
                duration: theme.transitions.duration.standard * 1.5,
              }),
          })}
        />
      </Box>

      <DestinationsGridItemLabel
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
        color={(theme) => theme.palette["neutral.700"].main}
        minHeight={{ xs: 50, md: 62 }}
        border={(theme) => ({ xs: `1px solid ${theme.palette["neutral.200"].main}`, md: "none" })}
      >
        <Typography variant="link.sm">{link?.title}</Typography>
        <SvgIcon variant="arrowOutward" size={{ xs: 10, md: 16 }} />
      </DestinationsGridItemLabel>
    </Box>
  )
}

/** A sub-component for the last Destinations Grid Item */
const DestinationsGridContactItem: FC = () => {
  const [showModal, setShowModal] = useState(false)

  return (
    <>
      <Box
        position="relative"
        overflow="hidden"
        borderRadius={1.5}
        sx={{ "&:hover": { img: { transform: "scale(1.05)" } } }}
      >
        <Button
          variant="base"
          aria-label="Talk to a Destination Specialist"
          onClick={() => setShowModal(true)}
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            aspectRatio: { xs: "1", md: "20 / 23" },
          }}
        >
          <ResponsiveImage
            image={{
              variants: [
                {
                  url: contactImage,
                  width: 4000,
                  height: 2286,
                  style: "default",
                  title: "default",
                  alt: "Cappadocia, Turkey",
                },
              ],
            }}
            display="block"
            sx={(theme) => ({
              width: "100%",
              transition: (theme) =>
                theme.transitions.create("transform", {
                  duration: theme.transitions.duration.standard * 1.5,
                }),
            })}
          />
        </Box>

        <DestinationsGridItemLabel
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          color={(theme) => theme.palette["neutral.700"].main}
          minHeight={{ xs: 50, md: 62 }}
        >
          <Typography variant="link.sm">Destination Specialists</Typography>
          <SvgIcon variant="arrowOutward" size={{ xs: 12, md: 16 }} />
        </DestinationsGridItemLabel>
      </Box>
      <InquiryModal open={showModal} onClose={() => setShowModal(false)} />
    </>
  )
}

/** A sub-component for the Trip Styles Label in the Grid Item */
const DestinationsGridItemLabel = styled(Stack)(
  ({ theme }) => `
    background-color: ${theme.palette["neutral.100"].main};
    max-height: 50px;
    padding: ${theme.spacing(4, 3)};
    position: relative;
    bottom: 0;
    left: 0;
    right: 0;

    ${theme.breakpoints.up("md")} {
      bottom: 16px;
      border-radius: ${theme.spacing(1)};
      max-height: 62px;
      left: 16px;
      position: absolute;
      padding: ${theme.spacing(3.5, 8)};
      right: 16px;
    }
  `
)
